import { useMemo } from "react";
import {
  MultiTicketType,
  type BookedFlightItineraryWithDepartureTime,
  PortalItineraryStatus,
} from "@b2bportal/air-booking-api";
import type { AirportMap } from "@b2bportal/core-flights";
import { useI18nContext } from "@hopper-b2b/i18n";
import {
  getFlightItineraryLegs,
  getOriginDestinationFromSlice,
} from "@hopper-b2b/utilities";
import type { TripHeaderProps } from "./types";
import { decodeCityName } from "@/utilities";

export type UseFlightTripHeaderReturn = Omit<TripHeaderProps, "statusPill"> & {
  flightStatus: BookedFlightItineraryWithDepartureTime["status"];
};

interface UseFlightTripHeaderProps {
  itinerary: BookedFlightItineraryWithDepartureTime;
  airportMap?: AirportMap;
  isCfarExercised: boolean;
  className?: string;
}

export const useFlightTripHeader = ({
  itinerary,
  airportMap,
  isCfarExercised,
  className,
}: UseFlightTripHeaderProps): UseFlightTripHeaderReturn => {
  const { t } = useI18nContext();

  return useMemo(() => {
    const confirmationCode =
      itinerary.bookedItinerary?.travelItinerary?.locators?.b2b ?? "";

    const flightStatus = isCfarExercised
      ? PortalItineraryStatus.Canceled
      : itinerary.status;

    const getDisplayName = (code: string) =>
      decodeCityName(airportMap?.[code]?.name, code);
    const legs = getFlightItineraryLegs(itinerary);
    const isHackerFare =
      itinerary.bookedItinerary?.multiTicketType === MultiTicketType.hackerFare;

    if (legs.length === 0) {
      return {
        className,
        title: "",
        confirmationCode,
        confirmationLabel: t("flightTrips.confirmationCode"),
        flightStatus,
      };
    }

    if (legs.length > 1 && !isHackerFare) {
      const segments = legs.map(({ slice }) => {
        const { originName, destinationName } = getOriginDestinationFromSlice(
          slice,
          getDisplayName
        );
        return t("core-flights.itinerary.card.itinerary", {
          origin: originName,
          destination: destinationName,
          // Airport names are HTML-decoded via decodeCityName
          interpolation: { escapeValue: false },
        });
      });
      return {
        className,
        title: "",
        confirmationCode,
        confirmationLabel: t("flightTrips.confirmationCode"),
        flightStatus,
        tripType: t("flightTrips.multiCity"),
        segments,
      };
    }

    const { originName, destinationName } = getOriginDestinationFromSlice(
      legs[0].slice,
      getDisplayName
    );
    const title = t("core-flights.itinerary.card.itinerary", {
      origin: originName,
      destination: destinationName,
      // Airport names are HTML-decoded via decodeCityName
      interpolation: { escapeValue: false },
    });

    return {
      className,
      title,
      confirmationCode,
      confirmationLabel: t("flightTrips.confirmationCode"),
      flightStatus,
    };
  }, [itinerary, airportMap, className, isCfarExercised, t]);
};
